import { computed, type ComputedRef, ref, type Ref, watch } from 'vue';
import { trackingSurveyEvent } from '@/api/tracking';
import { DOC_TYPE_LABELS } from '@/constants';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { isUserDocument } from '@/types';
import type { AdpDocument, FilterInfo, Part, UserDocument } from '@/types';
import type { SurveyPageName } from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { formatMarkdownForCopy } from '@/utils/survey/common';
import { SURVEY_FEATURE_NAME } from '@/utils/survey/constants';
import { getDocumentUrl } from '@/utils/user-document/common';

export function useSurveyHistoryCommon(params: {
  sources:
    | Ref<Array<AdpDocument | UserDocument>>
    | ComputedRef<Array<AdpDocument | UserDocument>>;
  filterInfo: Ref<FilterInfo> | ComputedRef<FilterInfo>;
  question: Ref<string> | ComputedRef<string>;
  originMarkdownSummary: Ref<string> | ComputedRef<string>;
  requestId: Ref<string> | ComputedRef<string>;
  sessionId: Ref<string> | ComputedRef<string>;
  pageName: SurveyPageName;
}) {
  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const { createSnackbar } = useSnackbar();

  // 検索に引っかかったドキュメントのみで表示するメッセージを生成する
  const hitDocTypes = computed<Array<keyof typeof DOC_TYPE_LABELS>>(() => {
    const types = new Set<keyof typeof DOC_TYPE_LABELS>();
    if (params.sources.value) {
      params.sources.value.forEach(source => {
        types.add(source.doc_type);
      });
    }
    const order = Object.keys(DOC_TYPE_LABELS);
    return Array.from(types).sort(
      (a, b) => order.indexOf(a) - order.indexOf(b),
    );
  });

  const searchResultsBreakdownMessage = computed(() => {
    const isSpecifiedFiles =
      params.filterInfo.value?.specifiedFiles &&
      params.filterInfo.value.specifiedFiles.length > 0;
    const datasources = isSpecifiedFiles
      ? '指定したファイル'
      : hitDocTypes.value.map(type => DOC_TYPE_LABELS[type] || type).join('、');
    const totalCount = params.sources.value.length;
    return `${datasources}から${totalCount}件の関連情報を検索しました`;
  });

  const dynamicTextSize = computed(() => {
    return params.question.value.length > 200
      ? 'c-title-bold--xm'
      : 'c-title-bold--xl';
  });

  const copySummary = async () => {
    try {
      // バックエンドで脚注部分を削っているので、フロントエンドで整形してコピーする
      const urls = params.sources.value.map(source =>
        isUserDocument(source) ? getDocumentUrl(source) : source.url,
      );
      const footnote = urls
        .map((url, index) => `[${index + 1}] ${url}`)
        .join('\n');

      const summaryTextForCopy = formatMarkdownForCopy(
        params.originMarkdownSummary.value,
        params.sources.value,
      );

      const formattedSummary =
        params.sources.value.length > 0
          ? `${params.question.value}\n\n${summaryTextForCopy}\n\nソース:\n${footnote}`
          : `${params.question.value}\n\n${summaryTextForCopy}`;

      await navigator.clipboard.writeText(formattedSummary);

      createSnackbar({
        message: `コピーしました`,
        type: 'success',
      });
    } catch (_e) {
      createSnackbar({
        message: `コピーできませんでした`,
        type: 'error',
      });
    } finally {
      const currentPath = getCurrentPathForTracking();
      const pageName = params.pageName;

      // 計測
      await trackingSurveyEvent('survey_result_copy', {
        page: {
          name: pageName,
          url: currentPath.replace('#', '').split('?')[0], // WARN: クエリパラメータがこの時点であることはないが、念の為機微情報であるため除外しておく
        },
        feature: SURVEY_FEATURE_NAME,
        request_id: params.requestId.value,
        session_id: params.sessionId.value,
      });
    }
  };

  return {
    hitDocTypes,
    searchResultsBreakdownMessage,
    dynamicTextSize,
    copySummary,
  };
}

/**
 * アコーディオンの自動開閉機能を管理するコンポーザブル
 */
export function useSurveyAccordion(params: {
  enableAnimation: Ref<boolean> | ComputedRef<boolean>;
  reasoning: Ref<Array<Part>> | ComputedRef<Array<Part>>;
  summary: Ref<Array<Part>> | ComputedRef<Array<Part>>;
}) {
  // 要約本文を表示するかどうか
  const isSummaryVisible = ref(!params.enableAnimation.value);

  // アコーディオンの状態管理
  const isAccordionOpen = ref(params.enableAnimation.value);
  const accordionRef = ref<HTMLElement | null>(null);
  const isAutoToggledAccordion = ref(false);

  /**
   * Chain-of-Thoughtの表示が完了したら自動的にアコーディオンを閉じる (自動で閉じるのは1度のみ)
   */
  watch(
    () => [params.reasoning.value, params.summary.value],
    () => {
      if (isAutoToggledAccordion.value) return;

      // CoTなしの生成 (検索なし生成)
      if (
        params.enableAnimation.value &&
        params.reasoning.value.length === 0 &&
        params.summary.value.length > 0
      ) {
        isSummaryVisible.value = true;
        return;
      }

      // CoTありの生成 (検索あり生成)
      if (
        params.enableAnimation.value &&
        params.reasoning.value.length > 0 &&
        params.summary.value.length > 0
      ) {
        // reasoningの最後の要素のdelayに基づいてタイミングを計算 (+1.5秒はCSSのanimation遅延2秒を考慮したUXの調整値)
        const baseDelayTime =
          params.reasoning.value[params.reasoning.value.length - 1].delay *
            1000 +
          1500;

        // 要約本文の表示を開始 (reasoningのDOM生成が完了して約1.5秒後に要約本文のDOM生成を開始)
        setTimeout(() => {
          isSummaryVisible.value = true;
        }, baseDelayTime);

        // 要約本文のアニメーションを開始して約1秒後にアコーディオンを閉じる (+3秒はCSSのanimation遅延2秒 と UXの調整用1秒の合算値)
        const autoToggledDelayTime = baseDelayTime + 3000;
        setTimeout(() => {
          if (isAccordionOpen.value) {
            toggleAccordion();
          }
          isAutoToggledAccordion.value = true;
        }, autoToggledDelayTime);
      }
    },
  );

  /**
   * アコーディオンの開閉状態を切り替える
   * アニメーションを伴う高さの変更を制御
   */
  const toggleAccordion = () => {
    if (!isAccordionOpen.value && !isAutoToggledAccordion.value) {
      isAutoToggledAccordion.value = true;
    }

    isAccordionOpen.value = !isAccordionOpen.value;
  };

  return {
    isSummaryVisible,
    isAccordionOpen,
    accordionRef,
    isAutoToggledAccordion,
    toggleAccordion,
  };
}
